import React, { useEffect, useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { ButtonNew as Button } from "components/buttonNew";
import "components/popup/popup.scss";
import { faTimes } from "@fortawesome/pro-regular-svg-icons";
import FeatureIcon from "components/icon/FeatureIcon";
import Icon from "components/icon/Icon";
import Spin from "components/spin/spin";

interface IPopupProps {
  open: boolean;
  type?: "success" | "warning" | "error" | "info" | "delete" | "export";
  title: string;
  description?: React.ReactNode;
  aligned?: "left" | "center";
  stacked?: boolean;
  closable?: boolean;
  onCancel?: any;
  onOk?: any;
  cancelText?: string;
  okText?: string;
  okDisabled?: boolean;
  cancelDisabled?: boolean;
  backDropCancel?: boolean;
  children?: any;
  darkStyle?: boolean;
  loading?: boolean;
  noFixedWidth?: boolean;
  className?: string;
  compactActions?: boolean;
  /** Hide the footer Confirm/Cancel buttons while keeping the X / backdrop cancel. */
  hideFooter?: boolean;
  /** Hide only the primary OK button while keeping Cancel for accessibility. */
  hideOk?: boolean;
  onOkType?:
  | "default"
  | "primary"
  | "secondary"
  | "tertiary"
  | "quaternary"
  | "dark-gray"
  | "error"
  | "error-outline"
  | "text"
  | "link"
  | "link-color"
  | "close";
}

const Popup: React.FC<IPopupProps> = props => {
  const { t } = useTranslation();
  const { open = false, onCancel, onOk, cancelText = t("Cancel"), okText = "Ok", stacked, loading, noFixedWidth, hideFooter, onOkType, className} = props;

  const [visible, setVisible] = useState(open);

  useEffect(() => {
    let mounted = true;

    if (open) {
      if (mounted) {
        setVisible(true);
      }
    } else {
      if (mounted) {
        setVisible(false);
      }
    }

    return () => {
      mounted = false;
    };
  }, [open]);

  return (
    <div
      className={classNames({
        "ui-popup_container--closed": !visible,
        "ui-popup_container--open": visible,
      })}
    >
      <div className={classNames("ui-popup_container", { "ui-popup_container-stacked": stacked })}>
        <div
          className={classNames("ui-popup", className)}
          style={noFixedWidth ? { width: "unset", maxWidth: "600px" } : undefined}
        >
          {props.onCancel ? (
            <div className="ui-popup-close-button">
              <Button type="close" size="large" onClick={props.onCancel}>
                <Icon icon={faTimes} size="medium" />
              </Button>
            </div>
          ) : null}

          <div
            className={classNames("ui-popup-content", {
              [`ui-popup-content-${props.aligned}`]: props.aligned,
            })}
          >
            <div className="ui-popup-heading">
              <div className="ui-popup-icon">
                {loading ? (
                  <div style={{ marginTop: "10px", height: "48px", width: "48px", overflow: "hidden" }}>
                    <Spin />
                  </div>
                ) : (
                  <div>
                    {props.type === "success" ? (
                      <FeatureIcon type="success" theme="light-circle-outline" size="large" icon="check" />
                    ) : null}
                    {props.type === "info" ? (
                      <FeatureIcon type="gray" theme="light-circle-outline" size="large" icon="question-circle" />
                    ) : null}
                    {props.type === "error" ? (
                      <FeatureIcon type="error" theme="light-circle-outline" size="large" icon="exclamation-triangle" />
                    ) : null}
                    {props.type === "delete" ? (
                      <FeatureIcon type="error" theme="light-circle-outline" size="large" icon="trash" />
                    ) : null}
                    {props.type === "warning" ? (
                      <FeatureIcon type="warning" theme="light-circle-outline" size="large" icon="exclamation" />
                    ) : null}
                    {props.type === "export" ? (
                      <FeatureIcon
                        type="primary"
                        theme="light-circle-outline"
                        size="large"
                        icon="arrow-up-from-bracket"
                      />
                    ) : null}
                  </div>
                )}
              </div>

              <p className="ui-titles-popup">{props.title}</p>
              {props.description ? (
                typeof props.description === "string" ? (
                  <p className="ui-description-popup">{props.description}</p>
                ) : (
                  <div className="ui-description-popup ui-description-popup--stacked">{props.description}</div>
                )
              ) : null}
            </div>

            {props.children ? <div className="ui-popup-body">{props.children}</div> : null}
          </div>

          {!hideFooter ? (
            <div
              className={classNames("ui-popup-footer", {
                "ui-popup-footer-compact": props.compactActions,
              })}
            >
              {props.onCancel ? (
                <Button
                  className="ui-popup-footer-close-button"
                  type="secondary"
                  block={!props.compactActions}
                  size={props.compactActions ? "small" : "medium"}
                  onClick={onCancel}
                  disabled={props.cancelDisabled}
                >
                  {cancelText}
                </Button>
              ) : null}
              {!props.hideOk ? (
              <Button
                type={onOkType ? onOkType : "primary"}
                block={!props.compactActions}
                size={props.compactActions ? "small" : "medium"}
                onClick={onOk}
                disabled={props.okDisabled}
              >
                {okText}
              </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
      <div
        className={classNames("ui-backdrop", { "ui-backdrop-stacked": stacked })}
        onClick={props.backDropCancel !== undefined && props.backDropCancel === false ? undefined : onCancel}
      ></div>
    </div>
  );
};

export default Popup;
